/**
 * Modern floating scrollbar.
 *
 * Hides the native scrollbar of `el` (class `cl-ms`) and draws a slim rounded thumb with no track that
 * fades in while scrolling or hovering, grows when grabbed and can be dragged. The styles live in
 * `src/styles.css` (`.cl-ms*`). The element still scrolls natively, so keyboard, wheel and touch behave as before.
 *
 * Usage:  const sb = attachModernScroll(el);   ...   sb.destroy();
 */
export interface ModernScrollOptions {
  /** px reserved at the top of `el` (e.g. a sticky header inside it) */
  top?: () => number;
  /** px reserved at the bottom of `el` (e.g. a fixed footer over it) */
  bottom?: () => number;
  z?: number;
  /** toggles `cl-fade-top` / `cl-fade-bot` on `el` while more content is hidden above / below (styled in styles.css) */
  fade?: boolean;
}

export interface ModernScrollHandle {
  update(): void;
  destroy(): void;
}

/** Only writes the class attribute when the state really changes (avoids feeding MutationObservers). */
function setClass(el: HTMLElement, name: string, on: boolean): void {
  if (el.classList.contains(name) !== on) {
    el.classList.toggle(name, on);
  }
}

export function attachModernScroll(el: HTMLElement, opts: ModernScrollOptions = {}): ModernScrollHandle {
  el.classList.add('cl-ms');

  const track = document.createElement('div');
  track.className = 'cl-ms-track';
  const thumb = document.createElement('div');
  thumb.className = 'cl-ms-thumb';
  track.appendChild(thumb);
  document.body.appendChild(track);
  if (opts.z) {
    track.style.zIndex = String(opts.z);
  }

  let hideTimer: any;
  let hovering = false;
  let dragging = false;
  let geo = { sh: 1, ch: 1, max: 1 };

  const show = () => {
    track.classList.add('vis');
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      if (!dragging && !hovering) {
        track.classList.remove('vis');
      }
    }, 1000);
  };

  const update = () => {
    const r = el.getBoundingClientRect();
    const sh = el.scrollHeight;
    const ch = el.clientHeight;
    const offscreen = r.width === 0 || r.right <= 0 || r.left >= window.innerWidth;
    if (opts.fade) {
      const more = sh > ch + 1 && !offscreen;
      setClass(el, 'cl-fade-top', more && el.scrollTop > 4);
      setClass(el, 'cl-fade-bot', more && el.scrollTop + ch < sh - 4);
    }
    if (sh <= ch + 1 || offscreen) {
      track.style.display = 'none';
      return;
    }
    const topOff = opts.top ? opts.top() : 0;
    const botOff = opts.bottom ? opts.bottom() : 0;
    track.style.display = 'block';
    const t = r.top + topOff + 4;
    const h = r.height - topOff - botOff - 8;
    track.style.top = t + 'px';
    track.style.height = h + 'px';
    track.style.left = (r.right - 10) + 'px';   // the track is 10px wide (styles.css .cl-ms-track): flush with the right edge of the element
    const th = Math.max(34, h * ch / sh);
    const max = h - th;
    thumb.style.height = th + 'px';
    thumb.style.transform = `translateY(${(el.scrollTop / (sh - ch)) * max}px)`;
    geo = { sh, ch, max };
  };

  const onScroll = () => { update(); show(); };
  const onEnter = () => { hovering = true; update(); show(); };
  const onLeave = () => { hovering = false; show(); };
  const onTrackPointerDown = (e: PointerEvent) => {
    if (e.target === thumb) { return; }
    const tr = thumb.getBoundingClientRect();
    el.scrollBy({ top: (e.clientY < tr.top ? -1 : 1) * el.clientHeight * 0.85, behavior: 'smooth' });
  };
  const onThumbPointerDown = (e: PointerEvent) => {
    e.preventDefault();
    thumb.setPointerCapture(e.pointerId);
    dragging = true;
    track.classList.add('drag');
    const y0 = e.clientY;
    const s0 = el.scrollTop;
    const move = (ev: PointerEvent) => {
      el.scrollTop = s0 + (ev.clientY - y0) * (geo.sh - geo.ch) / Math.max(1, geo.max);
    };
    const up = () => {
      dragging = false;
      track.classList.remove('drag');
      thumb.removeEventListener('pointermove', move);
      thumb.removeEventListener('pointerup', up);
      show();
    };
    thumb.addEventListener('pointermove', move);
    thumb.addEventListener('pointerup', up);
  };

  el.addEventListener('scroll', onScroll, { passive: true });
  // the pill appears while scrolling (and when the pointer is on the pill itself), not merely because the pointer is over the list
  el.addEventListener('mouseenter', update);
  track.addEventListener('mouseenter', onEnter);
  track.addEventListener('mouseleave', onLeave);
  track.addEventListener('pointerdown', onTrackPointerDown);
  thumb.addEventListener('pointerdown', onThumbPointerDown);
  window.addEventListener('resize', update);

  let ro: ResizeObserver | undefined;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(update);
    ro.observe(el);
  }
  update();

  return {
    update,
    destroy: () => {
      clearTimeout(hideTimer);
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('mouseenter', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
      track.remove();
      el.classList.remove('cl-ms', 'cl-fade-top', 'cl-fade-bot');
    }
  };
}
